import { timezones } from '@va/assets';
import { defaultTimezone } from '@va/constants';
import { updateWebsite } from '@va/dashboard/actions/api';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { getWebsiteTimezone } from '@va/dashboard/selectors/core';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useChangeTimezone } from '../apiClient';

type TimezoneType = {
  label: string;
  value: string;
  disabled?: boolean;
};

const useTimezoneCard = () => {
  const timezone = useSelector(getWebsiteTimezone);

  const currentTimezoneValue: string = useMemo(() => timezone ?? defaultTimezone, [timezone]);
  const currentTimezone = useMemo(
    () => timezones.find((item: TimezoneType) => item.value === currentTimezoneValue),
    [currentTimezoneValue],
  );

  const [selectedTimezone, setSelectedTimezone] = useState<TimezoneType | undefined>(currentTimezone);

  const dispatch = useDispatch();

  const { execute: changeTimezoneHandler, error, isSucceeded, clearError } = useChangeTimezone();

  const { showErrorNotification, showSuccessNotification } = useAddNotification();

  // Update the redux store when the api call is succeeded
  useEffect(() => {
    if (!isSucceeded) return;
    showSuccessNotification(2000);
    dispatch(updateWebsite({ timezone: selectedTimezone?.value }));
  }, [isSucceeded, selectedTimezone?.value, dispatch, showSuccessNotification]);

  // Show error notification and change to previous state
  useEffect(() => {
    if (!error) return;
    clearError();
    showErrorNotification(2000);
    setSelectedTimezone(currentTimezone);
  }, [error, clearError, dispatch, currentTimezone, showErrorNotification]);

  // Populating the initial state
  useEffect(() => {
    setSelectedTimezone(currentTimezone);
  }, [currentTimezone]);

  const onChange = (option: TimezoneType) => {
    setSelectedTimezone(option);
    changeTimezoneHandler(option.value);
  };

  return {
    onChange,
    selectedTimezone,
  };
};

export default useTimezoneCard;
